import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Banknote,
  Building2,
  BriefcaseBusiness,
  Home,
  Building,
  Users,
  TrendingUp,
  BadgeDollarSign,
  Wallet,
  Percent,
  Landmark,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const iconByMetric = {
  "Construction GDP": Banknote,
  "Construction Share of State GDP": Percent,
  "No.of Construction Establishments": BriefcaseBusiness,
  "Nonresidential Spending": Building,
  "Residential Spending": Home,
  Employment: Users,
  "Annual Job Growth (YoY)": TrendingUp,
  "Projected Wage Increase": BadgeDollarSign,
  "Average Annual Wage": Wallet,
  "Unemployment Rate (Industry)": Landmark,
};

const metricGroups = {
  dollars: [
    "Construction GDP",
    "Nonresidential Spending",
    "Residential Spending",
  ],
  counts: ["No.of Construction Establishments", "Employment"],
  wages: ["Average Annual Wage"],
  rates: [
    "Construction Share of State GDP",
    "Annual Job Growth (YoY)",
    "Projected Wage Increase",
    "Unemployment Rate (Industry)",
  ],
};

const metricMeta = {
  "Construction GDP": { unit: "usd" },
  "Nonresidential Spending": { unit: "usd" },
  "Residential Spending": { unit: "usd" },
  "Average Annual Wage": { unit: "usd" },
  "No.of Construction Establishments": { unit: "count" },
  Employment: { unit: "count" },
  "Construction Share of State GDP": { unit: "percent" },
  "Annual Job Growth (YoY)": { unit: "percent" },
  "Projected Wage Increase": { unit: "percent" },
  "Unemployment Rate (Industry)": { unit: "percent" },
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
};

const compactNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 100 ? 0 : 1,
  }).format(value);

const formatNumericValue = (value, unit) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "N/A";

  if (unit === "percent") return `${value.toFixed(1)}%`;

  if (unit === "usd") {
    if (Math.abs(value) >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(value >= 10_000_000_000 ? 0 : 1)}B`;
    }
    if (Math.abs(value) >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (Math.abs(value) >= 1_000) {
      return `$${compactNumber(value)}`;
    }
    return `$${value.toFixed(0)}`;
  }

  return compactNumber(value);
};

const parseMetricValue = (metric, rawValue) => {
  if (typeof rawValue === "number") return rawValue;
  if (!rawValue) return null;

  const text = String(rawValue).toLowerCase().replace(/,/g, "").trim();

  if (metricMeta[metric]?.unit === "percent") {
    const match = text.match(/-?\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
  }

  if (metricMeta[metric]?.unit === "usd") {
    const match = text.match(/\$?\s*(-?\d+(\.\d+)?)/);
    if (!match) return null;

    let value = Number(match[1]);
    if (text.includes("trillion")) value *= 1_000_000_000_000;
    else if (text.includes("billion")) value *= 1_000_000_000;
    else if (text.includes("million")) value *= 1_000_000;
    else if (text.includes("k")) value *= 1_000;

    return value;
  }

  const match = text.match(/-?\d+(\.\d+)?/);
  if (!match) return null;

  let value = Number(match[0]);
  if (text.includes("billion")) value *= 1_000_000_000;
  else if (text.includes("million")) value *= 1_000_000;
  else if (text.includes("thousand")) value *= 1_000;

  return value;
};

const buildChartRows = (rows, metrics) =>
  metrics
    .map((metric) => rows.find((row) => row.metric === metric))
    .filter(Boolean)
    .map((row) => ({
      metric: row.metric,
      label: row.metric
        .replace("No.of ", "")
        .replace("Construction Share of State ", "")
        .replace(" (Industry)", ""),
      utah: parseMetricValue(row.metric, row.utah),
      us: parseMetricValue(row.metric, row.us),
      utahLabel: formatValue(row.utah),
      usLabel: formatValue(row.us),
    }))
    .filter((row) => row.utah !== null && row.us !== null);

const chartHeightByMetric = {
  "Construction GDP": 170,
  "Nonresidential Spending": 170,
  "Residential Spending": 170,
  "Average Annual Wage": 170,
  "No.of Construction Establishments": 170,
  Employment: 170,
  "Construction Share of State GDP": 170,
  "Annual Job Growth (YoY)": 170,
  "Projected Wage Increase ": 170,
  "Unemployment Rate (Industry)": 170,
};

const ComparisonChart = ({ title, subtitle, rows, unit, height = 320 }) => {
  if (!rows.length) return null;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="mb-3 d-flex align-items-start justify-content-between gap-3">
        <div>
          <h3 className="text-base font-black text-gray-900 m-0">{title}</h3>
          <p className="text-sm text-gray-500 mt-1 mb-0">{subtitle}</p>
        </div>
        <span className="shrink-0 inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-indigo-700">
          2025
        </span>
      </div>

      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 8, right: 18, left: 20, bottom: 8 }}
            barCategoryGap="22%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fill: "#6b7280", fontSize: 12 }}
              tickFormatter={(value) => formatNumericValue(value, unit)}
            />
            <YAxis
              dataKey="label"
              type="category"
              width={150}
              tick={{ fill: "#374151", fontSize: 12, fontWeight: 600 }}
            />
            <Tooltip
              cursor={{ fill: "rgba(99, 102, 241, 0.06)" }}
              formatter={(value, name, item) => [
                name === "utah" ? item.payload.utahLabel : item.payload.usLabel,
                name === "utah" ? "Utah" : "U.S.",
              ]}
              contentStyle={{
                borderRadius: 16,
                border: "1px solid #e5e7eb",
                boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
              }}
            />
            <Legend
              formatter={(value) => (
                <span style={{ color: "#374151", fontWeight: 700 }}>
                  {value === "utah" ? "Utah" : "U.S."}
                </span>
              )}
            />
            <Bar dataKey="utah" name="utah" fill="#4f46e5" radius={[0, 8, 8, 0]} />
            <Bar dataKey="us" name="us" fill="#94a3b8" radius={[0, 8, 8, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const EconomicImpact = ({ data, title = "Utah vs. U.S. Economic Impact (2025)" }) => {
  const [viewMode, setViewMode] = useState("charts");

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .filter(Boolean)
      .map((r) => ({
        metric: r.metric ?? "Metric",
        utah: r.utah_2025,
        us: r.us_2025,
      }));
  }, [data]);

  const dollarChartRows = useMemo(() => buildChartRows(rows, metricGroups.dollars), [rows]);
  const countChartRows = useMemo(() => buildChartRows(rows, metricGroups.counts), [rows]);
  const wageChartRows = useMemo(() => buildChartRows(rows, metricGroups.wages), [rows]);
  const rateChartRows = useMemo(() => buildChartRows(rows, metricGroups.rates), [rows]);

  if (rows.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100"
      aria-labelledby="economic-impact-title"
    >
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-4">
        <div>
          <h2 id="economic-impact-title" className="text-xl font-black text-gray-900 m-0">
            {title}
          </h2>
          <p className="text-gray-500 text-sm mt-1 mb-0">
            Switch between visual charts and raw metric values depending on how you want to read the comparison.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setViewMode("charts")}
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
              viewMode === "charts"
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
            }`}
            aria-pressed={viewMode === "charts"}
          >
            View charts
          </button>
          <button
            type="button"
            onClick={() => setViewMode("cards")}
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
              viewMode === "cards"
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
            }`}
            aria-pressed={viewMode === "cards"}
          >
            View metric values
          </button>
          <button
            type="button"
            onClick={() => setViewMode("table")}
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
              viewMode === "table"
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
            }`}
            aria-pressed={viewMode === "table"}
          >
            View table
          </button>
        </div>
      </div>

      {viewMode === "charts" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-3">
            {dollarChartRows.map((row) => (
              <ComparisonChart
                key={`dollar-${row.metric}`}
                title={row.metric}
                subtitle="Dollar comparison shown on its own scale."
                rows={[row]}
                unit="usd"
                height={chartHeightByMetric[row.metric] || 170}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-3">
            {wageChartRows.map((row) => (
              <ComparisonChart
                key={`wage-${row.metric}`}
                title={row.metric}
                subtitle="Wage comparison isolated to its own scale."
                rows={[row]}
                unit="usd"
                height={chartHeightByMetric[row.metric] || 170}
              />
            ))}
            {countChartRows.map((row) => (
              <ComparisonChart
                key={`count-${row.metric}`}
                title={row.metric}
                subtitle="Industry scale comparison shown on its own scale."
                rows={[row]}
                unit="count"
                height={chartHeightByMetric[row.metric] || 170}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-3">
            {rateChartRows.map((row) => (
              <ComparisonChart
                key={`rate-${row.metric}`}
                title={row.metric}
                subtitle="Percent comparison shown on its own scale."
                rows={[row]}
                unit="percent"
                height={chartHeightByMetric[row.metric] || 170}
              />
            ))}
          </div>
        </>
      )}

      {viewMode === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((row, idx) => {
            const Icon = iconByMetric[row.metric] || Building2;
            return (
              <motion.div
                key={`${row.metric}-${idx}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -3 }}
                className="rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm"
              >
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-indigo-50 text-indigo-700">
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400">
                      Metric
                    </div>
                    <div className="font-extrabold text-gray-900 leading-tight truncate">
                      {row.metric}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">
                      Utah (2025)
                    </div>
                    <div className="text-base font-black text-indigo-900 mt-1 leading-snug">
                      {formatValue(row.utah)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-gray-600">
                      U.S. (2025)
                    </div>
                    <div className="text-base font-black text-gray-900 mt-1 leading-snug">
                      {formatValue(row.us)}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {viewMode === "table" && (
        <div className="mt-6 rounded-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500">
              Detailed table
            </div>
            <div className="text-xs text-gray-500">Tip: scroll horizontally on small screens</div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-white">
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100">
                    Metric
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100">
                    Utah 2025
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100">
                    U.S. 2025
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`tbl-${row.metric}-${idx}`} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-100">
                      {row.metric}
                    </td>
                    <td className="px-4 py-3 font-extrabold text-indigo-800 border-b border-gray-100">
                      {formatValue(row.utah)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">
                      {formatValue(row.us)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.section>
  );
};

export default EconomicImpact;
